import React, { useState, useEffect } from 'react';
import { Row, Card, CardBody, Button, Table, Spinner, Alert } from 'reactstrap';
import { connect } from 'react-redux';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import { AxiosAPI, getGroupID } from '../../../../../../helpers/AxiosAPI';
import {
  getAPI_ActiveFacilities,
  getAPI_WorkOrderTypes,
  postAPI_WorkOrderTypes_Import,
  putAPI_WorkOrderTypes,
} from '../../../../../../constants/APIs';
import WorkOrderEditModal from './WorkOrderEditModal';
import LanguageTabs from '../../../propinfo/components/LanguageTabs/LanguageTabs';
import { NotificationManager } from '../../../../../../components/common/react-notifications';

const WorkOrders = ({ propertyId, userName }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [activeFacilities, setActiveFacilities] = useState([]);
  const [propertyLanguages, setPropertyLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  // Edit modal state
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchWorkOrders = () => {
    if (!propertyId) return;

    setLoading(true);
    setError(null);

    AxiosAPI({
      method: 'get',
      url: getAPI_WorkOrderTypes,
      params: {
        groupId: getGroupID(),
        propertyId: propertyId,
      },
    })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setWorkOrders(data);
      })
      .catch((err) => {
        console.error('Error fetching work order types:', err);
        setError('Failed to load work orders. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const fetchActiveFacilities = () => {
    if (!propertyId) return;

    setLoading(true);

    AxiosAPI({
      method: 'get',
      url: getAPI_ActiveFacilities,
      params: {
        groupId: getGroupID(),
        channel: 'Flexkeeping',
      },
    })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setActiveFacilities(data);
      })
      .catch((err) => {
        console.error('Error fetching work order types:', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchActiveFacilities();
  }, [propertyId]);

  const handleImport = () => {
    if (!propertyId || importing) return;

    setImporting(true);
    setImportResult(null);
    setImportError(null);

    AxiosAPI({
      method: 'post',
      url: postAPI_WorkOrderTypes_Import,
      data: {
        groupId: getGroupID(),
        propertyId: propertyId,
        creator: userName || 'SystemAdmin',
      },
    })
      .then((res) => {
        const {
          importedCount = 0,
          skippedCount = 0,
          updatedCount = 0,
        } = res.data || {};
        setImportResult({ importedCount, skippedCount, updatedCount });
        fetchWorkOrders();
      })
      .catch((err) => {
        console.error(
          'Error importing work order types:',
          err?.response?.data?.Messages?.[0]
        );
        setImportError(
          err?.response?.data?.Messages?.[0] ||
            'Import failed. Please try again.'
        );
      })
      .finally(() => setImporting(false));
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setEditOpen(true);
  };

  const handleDeleteClick = async (id) => {
    const payload = {
      modifier: userName ?? 'dashboard',
    };
    const response = await AxiosAPI({
      method: 'delete',
      url: `${putAPI_WorkOrderTypes}/${id}`,
      data: payload,
    });

    if (response?.status == 200) {
      setWorkOrders((prev) => prev.filter((wo) => wo.id !== id));
      NotificationManager.success(
        '',
        'Work Order deleted successfully',
        3000,
        null,
        null,
        'success'
      );
    } else {
      NotificationManager.error(
        '',
        'Failed to delete work order',
        3000,
        null,
        null,
        'error'
      );
    }
  };

  const handleSave = async (updatedItem) => {
    const { appearance } = updatedItem;
    const updatedAppearance = appearance ? JSON.stringify(appearance) : '';
    setSaving(true);
    // Placeholder: replace with actual PUT API call when ready
    const response = await AxiosAPI({
      method: 'put',
      url: `${putAPI_WorkOrderTypes}/${updatedItem.id}`,
      data: {
        ...updatedItem,
        appearance: updatedAppearance,
        modifier: userName ?? 'system',
      },
    });

    if (response?.status == 200) {
      setWorkOrders((prev) =>
        prev.map((wo) => (wo.id === updatedItem.id ? updatedItem : wo))
      );
      setSaving(false);
      setEditOpen(false);
      setEditItem(null);
      NotificationManager.success(
        '',
        'Work Order updated successfully',
        3000,
        null,
        null,
        'success'
      );
    } else {
      NotificationManager.error(
        '',
        'Failed to update work order',
        3000,
        null,
        null,
        'error'
      );
    }
    // setTimeout(() => {
    //   setWorkOrders((prev) =>
    //     prev.map((wo) => (wo.id === updatedItem.id ? updatedItem : wo))
    //   );
    //   setSaving(false);
    //   setEditOpen(false);
    //   setEditItem(null);
    // }, 400);
  };

  useEffect(() => {
    fetchWorkOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const getWorkOrderName = (item, langCode = selectedLanguage) => {
    if (!Array.isArray(item.name) || item.name.length === 0) return '-';
    const langCodeEntry = item.name.find((n) => n.languageCode === langCode);
    const enEntry = item.name.find((n) => n.languageCode === 'en');
    return langCodeEntry ? langCodeEntry.statement : enEntry.statement;
  };

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).replace('_', ' ') : '-';

  const setLanguage = (e) => {
    var lan = e;
    if (e === 'zh-TW') {
      lan = 'hk';
    }
    setSelectedLanguage(lan);
  };

  return (
    <div>
      <Row>
        <Colxx xxs="12">
          {/* <Button outline color="primary" className="mb-2 mr-3" style={{ borderRadius: '50px' }}>
            + Add New
          </Button> */}
          <Button
            outline
            color="primary"
            size="xs"
            className="mb-2"
            // style={{ borderRadius: '50px' }}
            onClick={handleImport}
            disabled={importing}
          >
            {importing ? (
              <>
                <Spinner size="sm" className="mr-1" /> Importing...
              </>
            ) : (
              '+ Import'
            )}
          </Button>
        </Colxx>
      </Row>
      <Row className="mb-3">
        <Colxx xxs="12">
          <LanguageTabs
            selectedTab={selectedLanguage === 'hk' ? 'zh-TW' : selectedLanguage}
            setLanguage={setLanguage}
            getPropertyLanguage={(langs) => setPropertyLanguages(langs)}
          ></LanguageTabs>
        </Colxx>
      </Row>

      {importResult && (
        <Row className="mb-3">
          <Colxx xxs="12">
            <Alert color="success" toggle={() => setImportResult(null)}>
              Import complete — <strong>{importResult.importedCount}</strong>{' '}
              imported, <strong>{importResult.skippedCount}</strong> skipped,{' '}
              <strong>{importResult.updatedCount}</strong> updated.
            </Alert>
          </Colxx>
        </Row>
      )}

      {importError && (
        <Row className="mb-3">
          <Colxx xxs="12">
            <Alert color="danger" toggle={() => setImportError(null)}>
              {importError}
            </Alert>
          </Colxx>
        </Row>
      )}

      <Row>
        <Colxx xxs="12">
          {loading ? (
            <div className="text-center p-5">
              <Spinner color="primary" />
            </div>
          ) : error ? (
            <div className="text-center p-4 text-danger">{error}</div>
          ) : (
            <Card>
              <CardBody className="p-0">
                <Table
                  responsive
                  className="mb-0"
                  style={{ borderCollapse: 'collapse' }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f3f3f3' }}>
                      <th className="px-3 py-2">
                        <b>Work Order</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Item Type</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Facilities</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Source</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>External Item ID</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Price</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Image</b>
                      </th>
                      <th className="px-3 py-2">
                        <b>Actions</b>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4 text-muted">
                          No work orders found.
                        </td>
                      </tr>
                    ) : (
                      workOrders.map((item) => {
                        const facilityRecord = activeFacilities.find(
                          (facility) => facility.id == item.facilityId
                        );
                        return (
                          <tr
                            key={item.id}
                            style={{ borderBottom: '1px solid #f0f0f0' }}
                          >
                            <td className="px-3 py-2">
                              {getWorkOrderName(item)}
                            </td>
                            <td className="px-3 py-2">
                              {capitalize(item.type)}
                            </td>
                            <td className="px-3 py-2">
                              {facilityRecord?.title || item.facilityId || '-'}
                            </td>
                            <td className="px-3 py-2">
                              {item.source || 'Flexkeeping'}
                            </td>
                            <td className="px-3 py-2">{item.code || '-'}</td>
                            <td className="px-3 py-2">
                              {item.price != null ? `$${item.price}` : '$0'}
                            </td>
                            <td className="px-3 py-2">
                              {item.imageLink ? (
                                <img
                                  src={item.imageLink}
                                  alt="work order"
                                  style={{
                                    width: '68px',
                                    height: '56px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #dee2e6',
                                  }}
                                />
                              ) : (
                                <span
                                  className="text-muted"
                                  style={{ fontSize: '12px' }}
                                >
                                  —
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                size="xs"
                                color="primary"
                                outline
                                onClick={() => handleEditClick(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="xs"
                                color="danger"
                                outline
                                onClick={() => handleDeleteClick(item.id)}
                                className="ml-2"
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}
        </Colxx>
      </Row>

      {/* Edit Modal */}
      {editOpen && editItem && (
        <WorkOrderEditModal
          isOpen={editOpen}
          toggle={() => {
            setEditOpen(false);
            setEditItem(null);
          }}
          item={editItem}
          saving={saving}
          onSave={handleSave}
          langCode={selectedLanguage}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ settings }) => {
  const { propertyId, userName } = settings;
  return { propertyId, userName };
};

export default connect(mapStateToProps, {})(WorkOrders);
